import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Mail, RotateCcw, Save, Send, Shield, Store, User } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import Toggle from "@/components/app/Toggle";
import { btnOutline, btnPrimary, cardCls, inputCls, labelCls, pageSub, pageTitle } from "@/components/app/ui";

const GROUPS = [
  { key: "admin", label: "Super Admin", icon: Shield },
  { key: "vendor", label: "Vendor", icon: Store },
  { key: "customer", label: "Guest Customer", icon: User },
];

export default function AdminEmailTemplates() {
  const [list, setList] = useState(null);
  const [selId, setSelId] = useState(null);
  const [form, setForm] = useState(null);                  // {subject, heading, body, enabled}
  const [preview, setPreview] = useState({ subject: "", html: "", error: "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState("");
  const bodyRef = useRef(null);

  const load = useCallback(async (keep) => {
    const { data } = await api.get("/admin/email-templates");
    setList(data);
    setSelId((cur) => keep || cur || data[0]?.id);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load email templates.")); }, [load]);

  const sel = useMemo(() => list?.find((t) => t.id === selId), [list, selId]);
  useEffect(() => { if (sel) setForm({ subject: sel.subject, heading: sel.heading, body: sel.body, enabled: sel.enabled }); }, [sel?.id, sel?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  // live preview (debounced) rendered with sample data on the server
  useEffect(() => {
    if (!sel || !form) return undefined;
    const h = setTimeout(async () => {
      try {
        const { data } = await api.post(`/admin/email-templates/${sel.id}/preview`, { subject: form.subject, heading: form.heading, body: form.body, enabled: true });
        setPreview({ subject: data.subject, html: data.html, error: "" });
      } catch (err) { setPreview((p) => ({ ...p, error: apiErr(err.response?.data?.detail) })); }
    }, 400);
    return () => clearTimeout(h);
  }, [sel?.id, form?.subject, form?.heading, form?.body]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = sel && form && (form.subject !== sel.subject || form.heading !== sel.heading || form.body !== sel.body || form.enabled !== sel.enabled);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const insert = (name) => {
    const el = bodyRef.current;
    const tag = `{{${name}}}`;
    if (!el) return;
    const [a, b] = [el.selectionStart ?? form.body.length, el.selectionEnd ?? form.body.length];
    setForm((f) => ({ ...f, body: f.body.slice(0, a) + tag + f.body.slice(b) }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + tag.length, a + tag.length); });
  };

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.put(`/admin/email-templates/${sel.id}`, form);
      setList((l) => l.map((t) => (t.id === data.id ? data : t)));
      toast.success("Template saved. New emails use it immediately.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const reset = async () => {
    if (!window.confirm("Restore the default subject, heading and body for this email?")) return;
    setBusy("reset");
    try {
      const { data } = await api.delete(`/admin/email-templates/${sel.id}`);
      setList((l) => l.map((t) => (t.id === data.id ? data : t)));
      setForm({ subject: data.subject, heading: data.heading, body: data.body, enabled: true });
      toast.success("Default restored.");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const sendTest = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testTo.trim())) { toast.error("Enter a valid email address for the test."); return; }
    setBusy("test");
    try {
      const { data } = await api.post(`/admin/email-templates/${sel.id}/test`, form, { params: { to: testTo.trim() } });
      toast.success(data.message);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  if (!list) return <div className="text-slate-500 text-[13px]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="admin-email-templates">
      <div>
        <h1 className={pageTitle}>Email Templates</h1>
        <p className={pageSub}>The 17 emails the platform sends to super admins, vendors and guest customers. Edit the wording, switch an email off, or restore the default.</p>
      </div>

      <div className="grid lg:grid-cols-[280px_minmax(0,1fr)] gap-4 items-start">
        <aside className={`${cardCls} p-2 lg:sticky lg:top-4 max-h-[70vh] overflow-y-auto`} data-testid="template-list">
          {GROUPS.map((g) => (
            <div key={g.key} className="mb-2">
              <div className="flex items-center gap-1.5 px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500"><g.icon size={12} />{g.label}</div>
              {list.filter((t) => t.audience === g.key).map((t) => (
                <button key={t.id} onClick={() => setSelId(t.id)} data-testid={`template-${t.id}`}
                  className={`w-full text-left px-2.5 py-2 rounded-lg flex items-start gap-2 transition-colors ${t.id === selId ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-slate-50"}`}>
                  <span className="text-[10px] font-mono font-bold text-slate-400 mt-0.5 w-9 shrink-0">{t.id}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13px] font-semibold text-neutral-900 leading-tight">{t.name}</span>
                    <span className="flex gap-1 mt-0.5">
                      {!t.enabled && <span className="text-[10px] px-1.5 rounded bg-red-50 text-red-600 border border-red-200">Off</span>}
                      {t.customized && <span className="text-[10px] px-1.5 rounded bg-blue-50 text-blue-700 border border-blue-200">Edited</span>}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          ))}
        </aside>

        {sel && form && (
          <div className="space-y-4 min-w-0" data-testid="template-editor">
            <section className={`${cardCls} p-4`}>
              <div className="flex items-start gap-3 flex-wrap">
                <span className="w-9 h-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><Mail size={17} /></span>
                <div className="flex-1 min-w-[200px]">
                  <h2 className="text-[15px] font-bold text-neutral-900">{sel.id} · {sel.name}</h2>
                  <p className="text-[12px] text-slate-500">{sel.description}</p>
                  <p className="text-[11px] text-slate-400 mt-0.5">Event <code className="font-mono">{sel.event}</code></p>
                </div>
                <label className="flex items-center gap-2 text-[12px] text-slate-600">{form.enabled ? "Sending" : "Switched off"}
                  <Toggle checked={form.enabled} onChange={(v) => setForm((f) => ({ ...f, enabled: v }))} label="Send this email" t="template-enabled" /></label>
              </div>

              <div className="grid gap-3 mt-4">
                <div><label className={labelCls}>Subject</label><input value={form.subject} onChange={set("subject")} maxLength={200} data-testid="template-subject" className={inputCls} /></div>
                <div><label className={labelCls}>Heading</label><input value={form.heading} onChange={set("heading")} maxLength={120} data-testid="template-heading" className={inputCls} /></div>
                <div>
                  <label className={labelCls}>Body (HTML)</label>
                  <textarea ref={bodyRef} value={form.body} onChange={set("body")} rows={12} maxLength={20000} data-testid="template-body" spellCheck={false}
                    className={`${inputCls} !h-auto py-2 font-mono text-[12px] leading-relaxed`} />
                </div>
                <div>
                  <div className={labelCls}>Placeholders <span className="font-normal text-slate-400">— click to insert into the body; they work in the subject and heading too</span></div>
                  <div className="flex flex-wrap gap-1.5" data-testid="template-variables">
                    {sel.variables.map((v) => (
                      <button key={v.name} type="button" onClick={() => insert(v.name)} title={v.description + (v.html ? " (formatted block)" : "")}
                        className="text-[11px] font-mono px-2 py-1 rounded-md border border-slate-200 bg-slate-50 hover:border-blue-300 hover:text-blue-700">{`{{${v.name}}}`}</button>
                    ))}
                  </div>
                  {sel.required.length > 0 && <p className="text-[11px] text-orange-700 mt-1.5">This email must keep {sel.required.map((r) => `{{${r}}}`).join(" or ")} so the recipient can act on it.</p>}
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <button onClick={save} disabled={!dirty || busy === "save"} data-testid="template-save" className={btnPrimary}><Save size={14} /> {busy === "save" ? "Saving…" : "Save changes"}</button>
                <button onClick={reset} disabled={!sel.customized || busy === "reset"} data-testid="template-reset" className={btnOutline}><RotateCcw size={14} /> Restore default</button>
                <div className="flex gap-2 ml-auto items-center">
                  <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="send a test to…" data-testid="template-test-to" className={`${inputCls} !w-52`} />
                  <button onClick={sendTest} disabled={busy === "test"} data-testid="template-test" className={btnOutline}><Send size={14} /> {busy === "test" ? "Sending…" : "Send test"}</button>
                </div>
              </div>
            </section>

            <section className={`${cardCls} p-4`}>
              <div className="flex items-center justify-between mb-2"><h3 className="text-[13px] font-bold">Preview <span className="font-normal text-slate-500">(sample data)</span></h3></div>
              {preview.error ? <p role="alert" className="text-[12px] text-red-600" data-testid="template-preview-error">{preview.error}</p> : (
                <>
                  <div className="text-[12px] text-slate-600 mb-2">Subject: <b data-testid="template-preview-subject">{preview.subject}</b></div>
                  <iframe title="Email preview" sandbox="" srcDoc={`<body style="margin:12px">${preview.html}</body>`} data-testid="template-preview" className="w-full h-[420px] rounded-lg border border-slate-200 bg-white" />
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
